import {
  ArrowLeft,
  CalendarDays,
  Clock3,
  LockKeyhole,
  MapPin,
  MessageCircle,
  Search,
  ShieldAlert,
  ShieldCheck,
  SlidersHorizontal,
  UserRound,
  X,
} from "lucide-react";
import {
  Link,
  Navigate,
  Route,
  Routes,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { FormEvent, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { Brand } from "@/components/brand";
import { CreateFlow } from "@/components/create-flow";
import { DateCard } from "@/components/date-card";
import { PostRow } from "@/components/post-row";
import { ThemeToggle } from "@/components/theme-toggle";
import {
  LocationControls,
  LocationProvider,
  useSearchLocation,
} from "@/components/location-context";
import { dates, posts } from "@/data/demo";
import {
  CONNECTION_TYPES,
  POSTER_TYPES,
  SEEKING_TYPES,
} from "@/data/post-taxonomy";
import {
  ForgotPasswordPage,
  LoginPage,
  RegisterPage,
  ResetPasswordPage,
} from "./auth-pages";
import { AdminPage } from "./admin-page";
import { api } from "./api";
import {
  useAccountProfile,
  useConversations,
  useDate,
  useDates,
  useMessages,
  usePost,
  usePosts,
} from "./live-data";

export function App() {
  return (
    <LocationProvider>
      <Routes>
        <Route path="/" element={<Discover />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/design-system" element={<Gallery />} />
        <Route path="/messages" element={<Messages />} />
        <Route path="/messages/:id" element={<Conversation />} />
        <Route
          path="/create/post"
          element={
            <AppShell>
              <CreateFlow mode="post" />
            </AppShell>
          }
        />
        <Route
          path="/create/date"
          element={
            <AppShell>
              <CreateFlow mode="date" />
            </AppShell>
          }
        />
        <Route path="/dates" element={<Dates />} />
        <Route path="/dates/:id" element={<DateDetail />} />
        <Route path="/posts" element={<PostsBrowse />} />
        <Route path="/posts/:id" element={<PostDetail />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </LocationProvider>
  );
}

function Discover() {
  const [query, setQuery] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState<PostFilters>({ category: "", posterType: "", seeking: "" });
  const location = useSearchLocation();
  const geo = {
    latitude: location.latitude,
    longitude: location.longitude,
    radius: location.radius,
  };
  const livePosts = usePosts(query, filters.category, geo, filters.posterType, filters.seeking);
  const liveDates = useDates(query, geo);
  const quick = [
    ["Dates", "Dates"],
    ["NSA", "NSA"],
    ["Friendship", "Friendship"],
    ["Drinking pals", "Drinking pals"],
    ["Dating", "Dating"],
    ["Events", "Events"],
  ];
  return (
    <AppShell>
      <main className="page home-page">
        <header className="mobile-product-header">
          <Brand />
          <div className="mobile-header-actions">
            <Link to="/messages">
              <MessageCircle size={20} />
              <span>Messages</span>
            </Link>
            <Link to="/profile">
              <UserRound size={20} />
              <span>Account</span>
            </Link>
          </div>
        </header>
        <LocationControls />
        <div className="search-row home-search">
          <label className="search-box">
            <Search size={18} />
            <span className="sr-only">Search posts and dates</span>
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search posts and dates"
            />
          </label>
          <button
            className="filter-button"
            onClick={() => setFilterOpen(true)}
            aria-label="Open filters"
          >
            <SlidersHorizontal size={19} />
            <span>Filters</span>
          </button>
        </div>
        <nav className="quick-categories" aria-label="Quick categories">
          {quick.map(([label, category]) =>
            label === "Dates" ? (
              <Link to="/dates" key={label}>
                {label}
              </Link>
            ) : (
              <Link
                to={`/posts?category=${encodeURIComponent(category)}`}
                key={label}
              >
                {label}
              </Link>
            ),
          )}
        </nav>
        <p className="intro-strip">
          Local posts and planned meetups from singles and couples.
        </p>
        <section className="feed-section" aria-labelledby="new-nearby-heading">
          <div className="feed-heading">
            <div>
              <h1 id="new-nearby-heading">New near you</h1>
              <p>Brisbane · within 25 km</p>
            </div>
            <Link to="/posts">See all</Link>
          </div>
          <div className="post-list" aria-label="New near you">
            {livePosts.slice(0, 3).map((post) => (
              <PostRow post={post} key={post.id} />
            ))}
          </div>
        </section>
        <section
          className="feed-section"
          aria-labelledby="dates-nearby-heading"
        >
          <div className="feed-heading">
            <div>
              <h2 id="dates-nearby-heading">Upcoming Dates</h2>
              <p>Plans with a set time and place</p>
            </div>
            <Link to="/dates">See all</Link>
          </div>
          <div className="compact-dates" aria-label="Upcoming Dates">
            {liveDates.slice(0, 2).map((date) => (
              <DateCard date={date} key={date.id} />
            ))}
          </div>
        </section>
        <footer className="simple-footer">
          <Brand />
          <span>Local people. Clear plans.</span>
        </footer>
        {filterOpen && (
          <FilterSheet
            initial={filters}
            onClose={() => setFilterOpen(false)}
            onApply={(next) => { setFilters(next); setFilterOpen(false); }}
          />
        )}
      </main>
    </AppShell>
  );
}

function PostsBrowse() {
  const [params, setParams] = useSearchParams();
  const category = params.get("category") ?? "";
  const posterType = params.get("poster_type") ?? "";
  const seeking = params.get("seeking") ?? "";
  const [query, setQuery] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const location = useSearchLocation();
  const livePosts = usePosts(query, category, {
    latitude: location.latitude,
    longitude: location.longitude,
    radius: location.radius,
  }, posterType, seeking);
  const categories = ["", ...CONNECTION_TYPES];
  return (
    <AppShell>
      <main className="page browse-page">
        <header className="browse-header">
          <Link className="text-action" to="/" aria-label="Back to categories">
            <ArrowLeft />
          </Link>
          <div>
            <p className="eyebrow">Posts · Brisbane</p>
            <h1 className="page-title">{category || "All Posts"}</h1>
          </div>
        </header>
        <LocationControls compact />
        <div className="search-row">
          <label className="search-box">
            <Search size={18} />
            <span className="sr-only">Search this category</span>
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={`Search ${(category || "all posts").toLowerCase()}`}
            />
          </label>
          <button
            className="filter-button"
            aria-label="Open filters"
            onClick={() => setFilterOpen(true)}
          >
            <SlidersHorizontal size={19} />
            <span>Filters</span>
          </button>
        </div>
        <nav className="category-tabs" aria-label="Post categories">
          {categories.map((name) => (
            <button
              className={name === category ? "active" : ""}
              onClick={() => setParams(Object.fromEntries(Object.entries({ category: name, poster_type: posterType, seeking }).filter(([, value]) => value)))}
              key={name}
            >
              {name || "All"}
            </button>
          ))}
        </nav>
        <p className="browse-count">
          Showing the newest {livePosts.length} nearby
        </p>
        <section className="post-list" aria-label={`${category || "All"} Posts`}>
          {livePosts.map((post) => (
            <PostRow post={post} key={post.id} />
          ))}
          {livePosts.length === 0 && (
            <p className="empty-state">
              Nothing new here yet. Try another category.
            </p>
          )}
        </section>
        {filterOpen && (
          <FilterSheet
            initial={{ category, posterType, seeking }}
            onClose={() => setFilterOpen(false)}
            onApply={(next) => {
              setParams(Object.fromEntries(Object.entries({ category: next.category, poster_type: next.posterType, seeking: next.seeking }).filter(([, value]) => value)));
              setFilterOpen(false);
            }}
          />
        )}
      </main>
    </AppShell>
  );
}

type PostFilters = { category: string; posterType: string; seeking: string };

function FilterSheet({ onClose, onApply, initial }: { onClose: () => void; onApply: (filters: PostFilters) => void; initial: PostFilters }) {
  const [filters, setFilters] = useState(initial);
  return (
    <div
      className="sheet-backdrop filter-backdrop"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <section
        className="sheet filter-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="filter-heading"
      >
        <header>
          <div>
            <h2 id="filter-heading">Filters</h2>
            <p>Find the right local posts</p>
          </div>
          <button
            className="icon-button"
            onClick={onClose}
            aria-label="Close filters"
          >
            <X size={20} />
          </button>
        </header>
        <div className="filter-fields">
          <label className="field">
            <span>Connection</span>
            <select value={filters.category} onChange={(event) => setFilters({ ...filters, category: event.target.value })}>
              <option value="">All connections</option>
              {CONNECTION_TYPES.map((option) => <option key={option}>{option}</option>)}
            </select>
          </label>
          <label className="field">
            <span>Posted by</span>
            <select value={filters.posterType} onChange={(event) => setFilters({ ...filters, posterType: event.target.value })}>
              <option value="">Anyone</option>
              {POSTER_TYPES.map((option) => <option key={option}>{option}</option>)}
            </select>
          </label>
          <label className="field">
            <span>Looking for</span>
            <select value={filters.seeking} onChange={(event) => setFilters({ ...filters, seeking: event.target.value })}>
              <option value="">Anyone</option>
              {SEEKING_TYPES.filter((option) => option !== "Any").map((option) => <option key={option}>{option}</option>)}
            </select>
          </label>
        </div>
        <footer>
          <button className="button button-quiet" type="button" onClick={() => setFilters({ category: "", posterType: "", seeking: "" })}>
            Clear all
          </button>
          <button
            className="button button-primary"
            type="button"
            onClick={() => onApply(filters)}
          >
            Show results
          </button>
        </footer>
      </section>
    </div>
  );
}

function Gallery() {
  const swatches = [
    ["Primary background", "var(--color-bg-primary)"],
    ["Reading surface", "var(--color-surface-primary)"],
    ["Coral action", "var(--color-accent-primary)"],
    ["Private purple", "var(--color-accent-secondary)"],
    ["Available teal", "var(--color-accent-fresh)"],
    ["Urgent amber", "var(--color-accent-warm)"],
  ];
  return (
    <AppShell>
      <main className="page">
        <header>
          <p className="eyebrow">Design system · v0.1</p>
          <h1 className="page-title">Krosspaths classifieds system</h1>
          <p className="muted">
            The mobile-first interface for local Posts and planned Dates.
          </p>
        </header>
        <section className="gallery-section">
          <h2 className="section-title">Brand colours</h2>
          <div className="token-grid" style={{ marginTop: "1rem" }}>
            {swatches.map(([name, color]) => (
              <div className="swatch" key={name} style={{ background: color }}>
                {name}
              </div>
            ))}
          </div>
        </section>
        <section className="gallery-section">
          <p className="eyebrow">Typography</p>
          <div className="type-sample type-display">
            Make plans.
            <br />
            Keep your terms.
          </div>
        </section>
        <section className="gallery-section">
          <p className="eyebrow">Post pattern · personal notice</p>
          <div className="post-list">
            <PostRow post={posts[0]} />
            <PostRow post={posts[1]} />
          </div>
        </section>
        <section className="gallery-section">
          <p className="eyebrow">Date pattern · invitation</p>
          <div className="date-rail">
            <DateCard date={dates[0]} />
            <DateCard date={dates[1]} />
          </div>
        </section>
      </main>
    </AppShell>
  );
}

function Messages() {
  const conversations = useConversations();
  return (
    <AppShell>
      <main className="page">
        <p className="eyebrow">Private conversations</p>
        <h1 className="page-title">Messages</h1>
        <div className="post-list" style={{ marginTop: "2rem" }}>
          {conversations?.map((conversation) => (
            <Link
              className="post-row"
              to={`/messages/${conversation.id}`}
              key={conversation.id}
            >
              <span className="avatar">
                {conversation.name.slice(0, 2).toUpperCase()}
              </span>
              <span>
                <strong>{conversation.name}</strong>
                <span className="muted" style={{ display: "block" }}>
                  {conversation.preview}
                </span>
              </span>
            </Link>
          ))}
          {conversations?.length === 0 && (
            <p className="muted">
              No conversations yet. Open a Post and make the first move.
            </p>
          )}
          {conversations === null && (
            <Link className="post-row" to="/messages/south-bank-drinks">
              <span className="avatar">MA</span>
              <span>
                <strong>Maya</strong>
                <span className="post-title" style={{ display: "block" }}>
                  South Bank drinks
                </span>
                <span className="muted">That sounds easy. Around 8?</span>
              </span>
              <span className="tag tag-warm">New</span>
            </Link>
          )}
        </div>
      </main>
    </AppShell>
  );
}

function Conversation() {
  const { id } = useParams();
  const live = useMessages(id);
  if (live.messages !== null)
    return (
      <LiveConversation id={id!} messages={live.messages} myId={live.myId} />
    );
  return (
    <AppShell>
      <main className="conversation">
        <header className="conversation-header">
          <Link
            className="text-action"
            to="/messages"
            aria-label="Back to messages"
          >
            <ArrowLeft />
          </Link>
          <span className="avatar">MA</span>
          <div style={{ flex: 1 }}>
            <h1 className="conversation-name">Maya</h1>
            <div className="muted">Active 4 min ago</div>
          </div>
        </header>
        <aside className="context-block">
          <span className="eyebrow">From a Post · Tonight</span>
          <strong style={{ display: "block" }}>
            A late drink, good conversation, no pressure
          </strong>
          <span className="muted">South Brisbane · 2.4 km</span>
        </aside>
        <section className="message-stream">
          <p className="muted" style={{ textAlign: "center" }}>
            Today
          </p>
          <div className="bubble">
            Hey—your South Bank plan sounds refreshingly simple.
            <time>7:12 pm</time>
          </div>
          <div className="bubble own">
            That was the idea. Are you nearby tonight?
            <time>7:14 pm · Read</time>
          </div>
          <div className="bubble">
            I am. I could be there around eight.<time>7:15 pm</time>
          </div>
          <div className="album-object">
            <LockKeyhole />
            <h3 className="post-title">City nights</h3>
            <p className="muted">Private album · 6 images · 24 hours</p>
          </div>
        </section>
        <aside className="composer-lock paywall-inline">
          <strong>Upgrade to reply to this conversation.</strong>
          <button className="button button-primary">View plans</button>
          <div className="safety-actions">
            <button className="button button-quiet">Block</button>
            <button className="button button-quiet">
              <ShieldAlert size={16} /> Report
            </button>
          </div>
        </aside>
      </main>
    </AppShell>
  );
}

function LiveConversation({
  id,
  messages,
  myId,
}: {
  id: string;
  messages: Array<{
    id: number;
    body: string;
    sender_user_id: number;
    created_at: string;
  }>;
  myId: number | null;
}) {
  const [body, setBody] = useState("");
  const [status, setStatus] = useState("");
  async function send(event: FormEvent) {
    event.preventDefault();
    if (!body.trim()) return;
    try {
      await api.post(`/conversations/${id}/messages`, { type: "text", body });
      setBody("");
      setStatus("Sent");
    } catch {
      setStatus(
        "Replies require Plus, or this conversation is no longer available.",
      );
    }
  }
  return (
    <AppShell>
      <main className="conversation">
        <header className="conversation-header">
          <Link
            className="text-action"
            to="/messages"
            aria-label="Back to messages"
          >
            <ArrowLeft />
          </Link>
          <span className="avatar">KP</span>
          <div>
            <h1 className="conversation-name">Private conversation</h1>
            <div className="muted">Messages refresh automatically</div>
          </div>
        </header>
        <section className="message-stream">
          {messages.length === 0 && (
            <p className="muted">
              Conversation started. Send a respectful first message.
            </p>
          )}
          {messages.map((message) => (
            <div
              className={`bubble ${message.sender_user_id === myId ? "own" : ""}`}
              key={message.id}
            >
              {message.body}
              <time>
                {new Date(message.created_at).toLocaleTimeString("en-AU", {
                  hour: "numeric",
                  minute: "2-digit",
                })}
              </time>
            </div>
          ))}
        </section>
        <form className="composer-lock" onSubmit={send}>
          <label className="field">
            <span>Message</span>
            <textarea
              value={body}
              onChange={(event) => setBody(event.target.value)}
              maxLength={10000}
            />
          </label>
          <button className="button button-primary button-block">
            Send message
          </button>
          {status && (
            <p role="status" className="muted">
              {status}
            </p>
          )}
          <button className="button button-quiet button-block" type="button">
            <ShieldAlert size={16} /> Block or report
          </button>
        </form>
      </main>
    </AppShell>
  );
}

function Dates() {
  const location = useSearchLocation();
  const liveDates = useDates("", {
    latitude: location.latitude,
    longitude: location.longitude,
    radius: location.radius,
  });
  return (
    <AppShell>
      <main className="page">
        <header className="page-header">
          <div>
            <p className="eyebrow">Brisbane · July</p>
            <h1 className="page-title">Plans with a time and place.</h1>
          </div>
          <CalendarDays />
        </header>
        <LocationControls compact />
        <section style={{ display: "grid", gap: "1rem" }}>
          {liveDates.map((date) => (
            <DateCard date={date} key={date.id} />
          ))}
        </section>
      </main>
    </AppShell>
  );
}

function DateDetail() {
  const { id } = useParams();
  const date = useDate(id);
  const [introduction, setIntroduction] = useState("");
  const [requestStatus, setRequestStatus] = useState("");
  async function requestToJoin() {
    try {
      await api.post(`/dates/${id}/apply`, { introduction });
      setRequestStatus("Request sent to the host.");
    } catch {
      setRequestStatus(
        "Sign in and add at least a short introduction before requesting.",
      );
    }
  }
  return (
    <AppShell>
      <main className="page">
        <div className="detail-hero">
          <Link className="icon-button" to="/dates">
            <ArrowLeft />
          </Link>
          <div className="detail-date-summary">
            <span>
              <strong>{date.day}</strong>
              {date.month}
            </span>
            <p>
              {date.weekday}
              <br />
              {date.time}
            </p>
          </div>
          <h1>{date.title}</h1>
          <span className="date-status">{date.status}</span>
        </div>
        <p className="detail-copy">
          {date.description ??
            "A relaxed first drink with a small group and room to actually talk."}
        </p>
        <div className="fact-list">
          <div className="fact-row">
            <Clock3 />
            <strong>
              {date.weekday} at {date.time}
            </strong>
          </div>
          <div className="fact-row">
            <MapPin />
            <strong>{date.location}</strong>
          </div>
          <div className="fact-row">
            <ShieldCheck />
            <strong>Location privacy is logged and controlled</strong>
          </div>
        </div>
        <label className="field">
          <span>Short introduction for the host</span>
          <textarea
            value={introduction}
            onChange={(event) => setIntroduction(event.target.value)}
            minLength={10}
          />
        </label>
        <button
          className="button button-primary button-block"
          disabled={introduction.length < 10}
          onClick={requestToJoin}
        >
          Request to join
        </button>
        {requestStatus && (
          <p role="status" className="muted">
            {requestStatus}
          </p>
        )}
      </main>
    </AppShell>
  );
}

function PostDetail() {
  const { id } = useParams();
  const post = usePost(id);
  const navigate = useNavigate();
  const [messageStatus, setMessageStatus] = useState("");
  async function startConversation() {
    if (!post.ownerId) {
      setMessageStatus("Sign in to message this member.");
      return;
    }
    try {
      const { data } = await api.post("/conversations", {
        participant_user_id: post.ownerId,
        source_type: "post",
        source_id: post.databaseId,
      });
      navigate(`/messages/${data.data.id}`);
    } catch {
      setMessageStatus(
        "Sign in or upgrade your plan to start this conversation.",
      );
    }
  }
  return (
    <AppShell>
      <main className="page">
        <div className="detail-hero">
          <div className="detail-tools">
            <Link className="icon-button" to="/posts">
              <ArrowLeft />
            </Link>
            <button className="button button-secondary">Save</button>
          </div>
          <p className="eyebrow">{post.category}</p>
          <h1>{post.title}</h1>
          <div className="listing-location">
            <MapPin size={15} /> {post.location} · {post.distance} ·{" "}
            {post.freshness}
          </div>
        </div>
        <div className="detail-gallery" aria-label="Listing images">
          <div>{post.initials}</div>
          <span>1 of 3</span>
        </div>
        <h2 className="detail-section-title">About this Post</h2>
        <p className="detail-copy">
          {post.description} We prefer clear plans and respectful conversation.
        </p>
        <h2 className="detail-section-title">Looking for</h2>
        <div className="fact-list">
          <div className="fact-row">
            <UserRound />
            <strong>Singles or couples · respectful introductions</strong>
          </div>
          <div className="fact-row">
            <MapPin />
            <strong>{post.location} · suburb only</strong>
          </div>
          <div className="fact-row">
            <ShieldCheck />
            <strong>Exact plans are shared privately</strong>
          </div>
        </div>
        <section className="poster-summary">
          <span className="avatar">{post.initials}</span>
          <div>
            <strong>{post.owner}</strong>
            <p>{post.accountType} · Brisbane member</p>
          </div>
        </section>
        <div className="detail-safety">
          <button className="button button-quiet">Report Post</button>
          <button className="button button-quiet">Block member</button>
        </div>
        <div className="sticky-message">
          <button
            className="button button-primary button-block"
            onClick={startConversation}
          >
            <MessageCircle /> Message {post.owner}
          </button>
        </div>
        {messageStatus && (
          <p role="status" className="muted">
            {messageStatus}
          </p>
        )}
      </main>
    </AppShell>
  );
}

function Profile() {
  const account = useAccountProfile();
  const displayName = account?.profile.display_name ?? "Lena & Ash";
  const initials = displayName
    .split(/\s|&/)
    .filter(Boolean)
    .slice(0, 2)
    .map((value) => value[0])
    .join("")
    .toUpperCase();
  return (
    <AppShell>
      <main className="page">
        <header className="profile-header">
          <span className="avatar couple">{initials}</span>
          <div>
            <h1 className="page-title">{displayName}</h1>
            <p>Shared Couple · Brisbane</p>
          </div>
          <ThemeToggle />
        </header>
        <div className="profile-badges">
          <span>
            <ShieldCheck size={15} /> Established member
          </span>
          <span>Profile verified</span>
        </div>
        <section className="profile-section">
          <h2>About</h2>
          <p className="detail-copy">
            {account?.profile.introduction ??
              "Brisbane locals who like small bars, live music, coast drives, and plans on comfortable terms."}
          </p>
        </section>
        <section className="profile-section">
          <div className="feed-heading">
            <h2>Active Posts</h2>
            <Link to="/posts">View all</Link>
          </div>
          <div className="post-list">
            <PostRow post={posts[1]} />
          </div>
        </section>
        <section className="profile-section">
          <div className="feed-heading">
            <h2>Upcoming Dates</h2>
          </div>
          <DateCard date={dates[0]} />
        </section>
      </main>
    </AppShell>
  );
}
